import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, orderBy, query, deleteDoc, doc, updateDoc } from "firebase/firestore";
import db from "../firebase";
import "../App.css";
import html2canvas from "html2canvas";
import NavBar from "../components/NavBar";
import logo from "../logo.svg";
import { getTemplate } from "../templates";

/* ── 공용 필드 컴포넌트 ─────────────────────────────── */
const ICONS = ["💙", "⚔️", "🤝", "🌀", "❓"];

function DatePicker({ value, onChange }) {
  return (
    <input
      type="date"
      className="field-date"
      value={value || ""}
      max={new Date().toISOString().split("T")[0]}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.target.showPicker?.()}
    />
  );
}

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`star ${star <= (hover || value) ? "active" : ""}`}
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
        >★</span>
      ))}
    </div>
  );
}

function RelationshipInput({ value, onChange }) {
  const rows = value?.length ? value : [{ personA: "", icon: "💙", personB: "", memo: "" }];
  const update = (i, field, val) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  return (
    <div className="relationship-input">
      {rows.map((row, i) => (
        <div key={i} className="relationship-row">
          <input className="rel-input" placeholder="인물 A" value={row.personA} onChange={(e) => update(i, "personA", e.target.value)} />
          <select className="rel-select" value={row.icon} onChange={(e) => update(i, "icon", e.target.value)}>
            {ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
          </select>
          <input className="rel-input" placeholder="인물 B" value={row.personB} onChange={(e) => update(i, "personB", e.target.value)} />
          <input className="rel-input rel-memo" placeholder="한줄 메모" value={row.memo} onChange={(e) => update(i, "memo", e.target.value)} />
          <button className="rel-remove" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>✕</button>
        </div>
      ))}
      <button className="rel-add" onClick={() => onChange([...rows, { personA: "", icon: "💙", personB: "", memo: "" }])}>+ 인물 추가</button>
    </div>
  );
}

function TemplateField({ field, value, onChange }) {
  switch (field.type) {
    case "date":       return <DatePicker value={value} onChange={onChange} />;
    case "rating":     return <StarRating value={value || 0} onChange={onChange} />;
    case "relationship": return <RelationshipInput value={value} onChange={onChange} />;
    case "quote":
      return <textarea className="field-quote" placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    case "textarea":
      return <textarea className="field-textarea" placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    default:
      return <input className="field-input" type="text" placeholder={field.placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
}

/* ── 기록 읽기 뷰 ──────────────────────────────────── */
function RecordView({ record }) {
  const fields = getTemplate(record.genre);
  const data = record.record || {};

  const formatDate = (v) => v ? v.replace(/-/g, ".") : "";

  return (
    <div className="record-view">
      <div className="record-book-header">
        {record.image && (
          <img
            src={record.image.replace(/cover[^/]*\//, "cover200/")}
            alt={record.title}
            className="record-book-image"
            onError={(e) => { e.target.src = e.target.src.replace("/cover200/", "/cover/"); e.target.onerror = null; }}
          />
        )}
        <div className="record-book-meta">
          <div className="genre-tag">{record.genre}</div>
          <h3 className="record-book-title">{record.title}</h3>
          <p className="record-book-author">{record.author?.split("^").join(", ")}</p>
        </div>
      </div>
      <div className="popup-divider" />
      {fields.map((field) => {
        const val = data[field.label];
        if (!val && val !== 0) return null;

        if (field.type === "rating") {
          return (
            <div key={field.label} className="record-field">
              <span className="record-label">{field.label}</span>
              <span className="record-stars">
                {"★".repeat(val)}{"☆".repeat(5 - val)}
              </span>
            </div>
          );
        }
        if (field.type === "date") {
          return (
            <div key={field.label} className="record-field">
              <span className="record-label">{field.label}</span>
              <span className="record-value">{formatDate(val)}</span>
            </div>
          );
        }
        if (field.type === "quote") {
          return (
            <div key={field.label} className="record-field">
              <span className="record-label">{field.label}</span>
              <p className="record-quote">{val}</p>
            </div>
          );
        }
        if (field.type === "relationship" && Array.isArray(val)) {
          return (
            <div key={field.label} className="record-field">
              <span className="record-label">{field.label}</span>
              <div className="record-relationships">
                {val.map((row, i) => (
                  <p key={i} className="record-value">
                    {row.personA} {row.icon} {row.personB}
                    {row.memo ? ` — ${row.memo}` : ""}
                  </p>
                ))}
              </div>
            </div>
          );
        }
        return (
          <div key={field.label} className="record-field">
            <span className="record-label">{field.label}</span>
            <p className="record-value">{val}</p>
          </div>
        );
      })}
    </div>
  );
}

/* ── 메인 컴포넌트 ─────────────────────────────────── */
function LibraryPage() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [toast, setToast] = useState(null);
  const shareCardRef = useRef(null);
  const toastTimerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const q = query(collection(db, "records"), orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        setRecords(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchRecords();
    return () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); };
  }, []);

  const showToast = (message) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(message);
    toastTimerRef.current = setTimeout(() => setToast(null), 2500);
  };

  const closePopup = () => {
    setSelectedRecord(null);
    setConfirmDelete(false);
    setIsEditing(false);
    setEditData({});
  };

  const deleteRecord = async () => {
    try {
      await deleteDoc(doc(db, "records", selectedRecord.id));
      setRecords((prev) => prev.filter((r) => r.id !== selectedRecord.id));
      closePopup();
      showToast("기록이 삭제됐어요");
    } catch (e) {
      console.error(e);
    }
  };

  const startEditing = () => {
    setEditData({ ...selectedRecord.record });
    setIsEditing(true);
  };

  const saveEdit = async () => {
    try {
      await updateDoc(doc(db, "records", selectedRecord.id), { record: editData });
      const updated = { ...selectedRecord, record: editData };
      setRecords((prev) => prev.map((r) => (r.id === selectedRecord.id ? updated : r)));
      setSelectedRecord(updated);
      setIsEditing(false);
      showToast("수정됐어요 🌿");
    } catch (e) {
      console.error(e);
    }
  };

  const downloadCard = async () => {
    if (!shareCardRef.current) return;
    const canvas = await html2canvas(shareCardRef.current, { scale: 3 });
    const link = document.createElement("a");
    link.download = `여백_${selectedRecord.title}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const templateFields = selectedRecord ? getTemplate(selectedRecord.genre) : [];

  return (
    <div className="app">
      <NavBar />

      <div className="library-header">
        <h2 className="library-title">나의 서재</h2>
        {records.length > 0 && <span className="library-count">{records.length}권</span>}
      </div>

      {loading && <p className="loading">기록을 불러오는 중...</p>}

      {!loading && records.length === 0 && (
        <div className="library-empty">
          <p>아직 독서기록이 없어요</p>
          <button className="start-btn" onClick={() => navigate("/")}>첫 번째 책 기록하기 →</button>
        </div>
      )}

      <div className="library-grid">
        {records.map((record) => (
          <div key={record.id} className="library-grid-item" onClick={() => setSelectedRecord(record)}>
            {record.image
              ? <img
                  src={record.image.replace(/cover[^/]*\//, "cover200/")}
                  alt={record.title}
                  onError={(e) => { e.target.src = e.target.src.replace("cover200/", "cover/"); e.target.onerror = null; }}
                />
              : <div className="library-grid-noimage">{record.title}</div>
            }
            <div className="library-grid-overlay">
              <p className="library-grid-rating">{"★".repeat(record.record?.["추천하고 싶나요?"] || 0)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* 팝업 */}
      {selectedRecord && (
        <div className="popup-overlay" onClick={closePopup}>
          <div className="popup-card library-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              {isEditing && <h3 className="template-title">{selectedRecord.title}</h3>}
              <span />
              <button className="popup-close" onClick={closePopup}>✕</button>
            </div>

            <div className="popup-body">
              {isEditing ? (
                <>
                  {templateFields.map((field) => (
                    <div key={field.label} className="field-group">
                      <label className="field-label">{field.label}</label>
                      <TemplateField
                        field={field}
                        value={editData[field.label]}
                        onChange={(val) => setEditData((prev) => ({ ...prev, [field.label]: val }))}
                      />
                    </div>
                  ))}
                  <div className="edit-actions">
                    <button className="delete-cancel-btn" onClick={() => setIsEditing(false)}>취소</button>
                    <button className="save-btn" style={{ marginTop: 0 }} onClick={saveEdit}>저장하기</button>
                  </div>
                </>
              ) : (
                <>
                  <RecordView record={selectedRecord} />

                  <div className="popup-actions">
                    <button className="action-icon-btn" onClick={startEditing}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      <span>수정</span>
                    </button>
                    <button className="action-icon-btn" onClick={downloadCard}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      <span>카드 저장</span>
                    </button>
                    <button className="action-icon-btn danger" onClick={() => setConfirmDelete(true)}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6M14 11v6" />
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                      </svg>
                      <span>삭제</span>
                    </button>
                  </div>

                  {confirmDelete && (
                    <div className="delete-confirm">
                      <span>삭제된 기록은 되돌릴 수 없어요. 정말 삭제할까요?</span>
                      <div className="delete-confirm-actions">
                        <button className="delete-cancel-btn" onClick={() => setConfirmDelete(false)}>취소</button>
                        <button className="delete-confirm-btn" onClick={deleteRecord}>삭제</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 공유카드: 화면 밖에 숨겨두고 캡처용으로만 사용 */}
      {selectedRecord && (
        <div style={{ position: "fixed", left: "-9999px", top: 0, pointerEvents: "none" }}>
          <div ref={shareCardRef} className="share-card">
            <img src={logo} alt="여백" className="share-logo" />
            {selectedRecord.image && (
              <img
                src={selectedRecord.image.replace(/cover[^/]*\//, "cover200/")}
                alt={selectedRecord.title}
                className="share-book-image"
              />
            )}
            <div className="share-genre-tag">{selectedRecord.genre}</div>
            <h3 className="share-title">{selectedRecord.title}</h3>
            <p className="share-author">{selectedRecord.author?.split("^").join(", ")}</p>
            {selectedRecord.record?.["추천하고 싶나요?"] > 0 && (
              <p className="share-rating">
                {"★".repeat(selectedRecord.record["추천하고 싶나요?"])}
                {"☆".repeat(5 - selectedRecord.record["추천하고 싶나요?"])}
              </p>
            )}
            {selectedRecord.record?.["한 줄 감상"] && (
              <p className="share-review">"{selectedRecord.record["한 줄 감상"]}"</p>
            )}
            <div className="share-footer">
              <img src={logo} alt="여백" className="share-footer-logo" />
              <p className="share-footer-text">YEOBAEK Reading Journal</p>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

export default LibraryPage;
